import {
    filters as filtersBlank,
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '@/utils/transfers/transfers-blank-states'
import {appInstance} from '@/utils/app-accessor'
import {runtimeStore, transfersRuntimeStore} from '@/composables/store-accessor'
import {isAfter, isBefore, parseISO} from 'date-fns'
import {UTCToDate} from '@/utils/helpers'
import {salesTermsRsTransformer} from '@/utils/api-helpers'
import type {Ref} from 'vue'
import {useProductStore} from '~/composables/product-store'

export const useTransfersStore = defineStore('transfers', () => {
    function _getBasketItem(offerKey: any) {
        return (
            basket.value.find(item => item.offerKey === offerKey) || {
                searchRequest: searchRequestBlank(),
                product: {offers: [], info: {}},
                info: {},
            }
        )
    }

    const searchRequest = ref(searchRequestBlank())
    const searchResponse = ref(searchResponseBlank())
    const filters: Ref = ref(filtersBlank())
    const sortFnName = ref('priceAsc')
    const searchExpirationTime: Ref = ref(null)
    const prepareBookRequest: Ref = ref({
        departure: {},
        arrival: {},
    })
    const prepareBookReturnTransferRequest: Ref = ref({
        departure: {},
        arrival: {},
    })
    const seats: Ref = ref([[], []])
    const prepareBookResponse: Ref = ref({})
    const basket: Ref<any[]> = ref([])
    const bookingAdditionalOptions: Ref<any[]> = ref([])
    const offersToCompare: Ref<any[]> = ref([])

    function ADD_TO_BASKET({info, offerKey}: any) {
        basket.value = basket.value.filter(item => {
            const offer = item.product.offers.find((offer: any) => offer.offerKey === item.offerKey)
            return isBefore(new Date(), UTCToDate(parseISO(offer.expirationTime)))
        })
        if (_getBasketItem(offerKey).offerKey) return

        const product = transfersRuntimeStore.offers.find(
            (product: any) => product.offers.findIndex((offer: any) => offer.offerKey === offerKey) !== -1
        )

        basket.value.push({
            offerKey,
            product,
            info,
            searchRequest: searchRequest.value,
        })
    }

    function REFRESH_BASKET_PRICE({offerKey, prepareBookResponse}: any) {
        const basketItem = _getBasketItem(offerKey)
        if (!basketItem.offerKey) return
        const offer = basketItem.product.offers.find((offer: any) => offer.offerKey === offerKey)
        const {price} = salesTermsRsTransformer(prepareBookResponse.currentSalesTerms)
        offer.currentPrice = price
    }

    function SET_SEARCH_REQUEST(rq: any) {
        searchRequest.value = rq
    }

    function SET_FILTERS(val: any) {
        filters.value = val
    }

    function SET_FILTER(data: any) {
        filters.value[data.key] = data.value
    }

    function SET_SORT(sort: any) {
        sortFnName.value = sort
    }

    function SET_SEARCH_EXPIRATION_TIME(date: any) {
        searchExpirationTime.value = date
    }

    function SET_PREPARE_BOOK_REQUEST(_prepareBookRequest: any) {
        prepareBookRequest.value = _prepareBookRequest
    }

    function SET_PREPARE_BOOK_RETURN_TRANSFER_REQUEST(_prepareBookReturnTransferRequest: any) {
        prepareBookReturnTransferRequest.value = _prepareBookReturnTransferRequest
    }

    function SET_PREPARE_BOOK_REQUEST_PROP({prop, val}: any) {
        prepareBookRequest.value[prop] = val
    }

    function SET_PREPARE_BOOK_RETURN_TRANSFER_REQUEST_PROP({prop, val}: any) {
        prepareBookReturnTransferRequest.value[prop] = val
    }

    function SET_PREPARE_BOOK_LOCATION_PROP({pointType, prop, val}: any) {
        if (!prepareBookRequest.value[pointType]) {
            prepareBookRequest.value[pointType] = {}
        }
        prepareBookRequest.value[pointType][prop] = val
    }

    function SET_PREPARE_BOOK_RETURN_TRANSFER_LOCATION_PROP({pointType, prop, val}: any) {
        prepareBookReturnTransferRequest.value[pointType][prop] = val
    }

    function SET_PREPARE_BOOK_RESPONSE(_prepareBookResponse: any) {
        prepareBookResponse.value = _prepareBookResponse
    }

    function NEW_SEARCH(_searchRequest: any) {
        searchRequest.value = _searchRequest
        filters.value = filtersBlank()
        offersToCompare.value = []
    }

    function RESET() {
        searchRequest.value = searchRequestBlank()
        prepareBookRequest.value = {
            departure: {},
            arrival: {},
        }
    }

    function RESET_FILTERS() {
        filters.value = filtersBlank()
    }

    function ADD_PRODUCT_TO_COMPARE(_productId, offersKeys) {
        const index = offersToCompare.value.findIndex(({productId}) => productId === _productId)

        if (index !== -1) {
            if (offersKeys.length) offersToCompare.value[index].offersKeys = offersKeys
            else offersToCompare.value.splice(index, 1)
        } else {
            offersToCompare.value.push({productId: _productId, offersKeys})
        }
    }

    function CLEAR_OFFERS_TO_COMPARE() {
        offersToCompare.value = []
    }

    //TODO Need refactoring and fixing - now REFRESH_BASKET_PRICE not work correct on booking by basket
    async function prepareBook({rq, isReturnTransfer, returnTransferExtraServices}: any) {
        const request = {...rq}
        if (request.arrival?.type === 'hotel') {
            request.arrival = {
                type: request.arrival.type,
                hotelName: request.arrival.hotelName,
                hotelCode: request.arrival.hotelCode,
            }
        }
        if (request.departure?.type === 'hotel') {
            request.departure = {
                type: request.departure.type,
                hotelName: request.departure.hotelName,
                hotelCode: request.departure.hotelCode,
            }
        }
        if (seats.value[0].length) {
            request.seatServices = clone(seats.value[0]).map((seatItem, index) => ({
                seat: {
                    travelerRPH: index,
                    seatNumber: seatItem.seatNumber,
                },
                serviceRPH: bookingAdditionalOptions.value[0].seatSelectionServices[0].serviceRPH,
            }))
        } else delete request.seatServices
        runtimeStore.SET_BOOKING_ACTIVE(true)
        try {
            //TODO API workaround - ignore params in API layer
            request.tourists?.forEach((tourist: any) => {
                if (tourist.passport) {
                    delete tourist.passport
                }
            })
            SET_PREPARE_BOOK_REQUEST(request)
            let rs
            if (isReturnTransfer) {
                const {basketKey} = await appInstance.$api.basket.post()
                const transferRs = await appInstance.$api.prepareBookTransfer.put(basketKey, request)
                let selectedPrebookSeats = null
                if (seats.value[1].length) {
                    selectedPrebookSeats = clone(seats.value[1]).map((seatItem, index) => ({
                        seat: {
                            travelerRPH: index,
                            seatNumber: seatItem.seatNumber,
                        },
                        serviceRPH: bookingAdditionalOptions.value[1].seatSelectionServices[0].serviceRPH,
                    }))
                }
                const transferReturnRequest = {
                    ...prepareBookRequest.value,
                    ...prepareBookReturnTransferRequest.value,
                    ...(returnTransferExtraServices?.length && {extraServices: returnTransferExtraServices}),
                    ...(selectedPrebookSeats?.length && {seatServices: selectedPrebookSeats}),
                }
                SET_PREPARE_BOOK_RETURN_TRANSFER_REQUEST(transferReturnRequest)

                /*if (transferReturnRequest.arrival.type === 'hotel') {
                    transferReturnRequest.arrival = {
                        type: transferReturnRequest.arrival.type,
                        hotelName: transferReturnRequest.arrival.name,
                        hotelCode: transferReturnRequest.arrival.hotelCode,
                    }
                }

                if (transferReturnRequest.departure.type === 'hotel') {
                    transferReturnRequest.departure = {
                        type: transferReturnRequest.departure.type,
                        hotelName: transferReturnRequest.departure.name,
                        hotelCode: transferReturnRequest.departure.hotelCode,
                    }
                }*/

                const transferReturnRS = await appInstance.$api.prepareBookTransfer.put(
                    basketKey,
                    transferReturnRequest
                )

                // rs = {...transferReturnRS.initResponse, basketKey}
                // rs.transferRs = {...transferRs.initResponse}
                // rs.transferReturnRs = {...transferReturnRS.initResponse}
                rs = {
                    prepareBookResponses: [transferRs.initResponse, transferReturnRS.initResponse],
                    basketKey,
                }
            } else {
                rs = await appInstance.$api.prepareBookTransfer.post(request)
            }
            SET_PREPARE_BOOK_RESPONSE(rs)
            return rs
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    const isOffersExpired = computed(() => {
        return () =>
            hasOffers.value && !transfersRuntimeStore.searchActive && isAfter(new Date(), searchExpirationTime.value)
    })

    const basketItem = computed(() => {
        return (offerKey: any) => _getBasketItem(offerKey)
    })

    const hasOffers = computed(() => {
        return !!searchResponse.value.product.length
    })

    function ADD_BOOKING_ADDITIONAL_OPTIONS(options: any) {
        bookingAdditionalOptions.value.push(options)
    }

    function clearBookingAdditionalOptions() {
        bookingAdditionalOptions.value = []
    }

    async function loadBookingAdditionalOptions(offerKey: any) {
        try {
            const options = await appInstance.$api.transferAdditionalOptions.get({offerKey})
            ADD_BOOKING_ADDITIONAL_OPTIONS(options)
        } catch (error) {
            console.error(error)
        }
    }

    return {
        ...useProductStore(searchResponse),
        searchRequest,
        searchResponse,
        filters,
        sortFnName,
        searchExpirationTime,
        prepareBookRequest,
        prepareBookReturnTransferRequest,
        prepareBookResponse,
        basket,
        bookingAdditionalOptions,
        offersToCompare,
        ADD_TO_BASKET,
        REFRESH_BASKET_PRICE,
        SET_SEARCH_REQUEST,
        SET_FILTERS,
        SET_FILTER,
        SET_SORT,
        SET_SEARCH_EXPIRATION_TIME,
        SET_PREPARE_BOOK_REQUEST,
        SET_PREPARE_BOOK_RETURN_TRANSFER_REQUEST,
        SET_PREPARE_BOOK_REQUEST_PROP,
        SET_PREPARE_BOOK_RETURN_TRANSFER_REQUEST_PROP,
        SET_PREPARE_BOOK_LOCATION_PROP,
        SET_PREPARE_BOOK_RETURN_TRANSFER_LOCATION_PROP,
        SET_PREPARE_BOOK_RESPONSE,
        ADD_PRODUCT_TO_COMPARE,
        CLEAR_OFFERS_TO_COMPARE,
        NEW_SEARCH,
        RESET,
        RESET_FILTERS,
        prepareBook,
        ADD_BOOKING_ADDITIONAL_OPTIONS,
        clearBookingAdditionalOptions,
        loadBookingAdditionalOptions,
        isOffersExpired,
        basketItem,
        hasOffers,
        seats,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useTransfersStore, import.meta.hot))
